import { tokens } from "@fluentui/react-components";
import {
  ArrowSync12Regular,
  ArrowSync16Regular,
  CheckmarkCircle12Regular,
  CheckmarkCircle16Regular,
  Circle12Regular,
  Circle16Regular,
  CircleHalfFill12Regular,
  CircleHalfFill16Regular,
  CircleHint12Regular,
  CircleHint16Regular,
  Clock12Regular,
  Clock16Regular,
  DismissCircle12Regular,
  DismissCircle16Regular,
  ErrorCircle12Regular,
  ErrorCircle16Regular,
  Eye12Regular,
  Eye16Regular,
  Prohibited12Regular,
  Prohibited16Regular,
  Subtract12Regular,
  Subtract16Regular,
} from "@fluentui/react-icons";
import type { CSSProperties, ReactElement } from "react";
import type { RecordSeverity, RecordStatus } from "@/engine/types";

export type GlyphSize = 12 | 16;

const STATUS_ICON = {
  open: { 12: Circle12Regular, 16: Circle16Regular },
  requested: { 12: Circle12Regular, 16: Circle16Regular },
  identified: { 12: Eye12Regular, 16: Eye16Regular },
  proposed: { 12: CircleHint12Regular, 16: CircleHint16Regular },
  inProgress: { 12: CircleHalfFill12Regular, 16: CircleHalfFill16Regular },
  mitigating: { 12: CircleHalfFill12Regular, 16: CircleHalfFill16Regular },
  reviewing: { 12: Clock12Regular, 16: Clock16Regular },
  occurred: { 12: ErrorCircle12Regular, 16: ErrorCircle16Regular },
  resolved: { 12: CheckmarkCircle12Regular, 16: CheckmarkCircle16Regular },
  applied: { 12: CheckmarkCircle12Regular, 16: CheckmarkCircle16Regular },
  accepted: { 12: CheckmarkCircle12Regular, 16: CheckmarkCircle16Regular },
  approved: { 12: CheckmarkCircle12Regular, 16: CheckmarkCircle16Regular },
  closed: { 12: CheckmarkCircle12Regular, 16: CheckmarkCircle16Regular },
  rejected: { 12: DismissCircle12Regular, 16: DismissCircle16Regular },
  superseded: { 12: ArrowSync12Regular, 16: ArrowSync16Regular },
  deprecated: { 12: Prohibited12Regular, 16: Prohibited16Regular },
};

/** 진행·확정은 브랜드, 발생은 빨강, 해결·반영은 초록. 나머지는 중립. */
function statusColor(status: RecordStatus): string {
  switch (status) {
    case "inProgress":
    case "mitigating":
    case "reviewing":
    case "accepted":
    case "approved":
      return tokens.colorBrandForeground1;
    case "occurred":
      return tokens.colorPaletteRedForeground1;
    case "resolved":
    case "applied":
      return tokens.colorPaletteGreenForeground1;
    default:
      return tokens.colorNeutralForeground3;
  }
}

/** 기록 상태 표식. 표 셀은 12, 메뉴·셀렉트는 16. 색은 표식에만 싣는다. */
export function recordStatusGlyph(status: RecordStatus, size: GlyphSize = 16, tone = false): ReactElement {
  const Icon = STATUS_ICON[status][size];
  const style: CSSProperties | undefined = tone ? { color: statusColor(status) } : undefined;
  return <Icon style={style} />;
}

/** 세 칸 막대. 채운 개수로 높음·보통·낮음을 나누고, 높음만 빨강이다. */
function SeverityBars({ active, color, size }: { active: 1 | 2 | 3; color: string; size: GlyphSize }) {
  const heights = [6, 10, 14];
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden focusable="false">
      {heights.map((height, index) => (
        <rect
          key={height}
          x={1.5 + index * 5}
          y={15 - height}
          width="3"
          height={height}
          rx="0.75"
          fill={index < active ? color : tokens.colorNeutralStroke2}
        />
      ))}
    </svg>
  );
}

/** 심각도 표식. 막대 수로 구분하고 높음은 빨강이다. 미정은 빼기. */
export function severityGlyph(severity: RecordSeverity | undefined, size: GlyphSize = 16): ReactElement {
  switch (severity) {
    case "high":
      return <SeverityBars active={3} color={tokens.colorPaletteRedForeground1} size={size} />;
    case "medium":
      return <SeverityBars active={2} color={tokens.colorNeutralForeground2} size={size} />;
    case "low":
      return <SeverityBars active={1} color={tokens.colorNeutralForeground3} size={size} />;
    default: {
      const Icon = size === 12 ? Subtract12Regular : Subtract16Regular;
      return <Icon style={{ color: tokens.colorNeutralForeground3, width: size, height: size }} />;
    }
  }
}
